// @flow

import { type IncomingPayment, getMerchant, getHash, getStatus, getReceipts } from '@tectonics/prs/incoming-payment';
import { getGoogleAnalyticsTrackingIds, getGoogleAnalyticsTrackingSite } from '@tectonics/prs/merchant';
import { type AnalyticsEventType, AnalyticsProvider } from '@tectonics/analytics';
import { getMainReceipt } from '@tectonics/prs/receipts';
import { getReceiptAmountExt } from '@tectonics/prs/receipts/receipt';
import { getAmountValue, getAmountCurrencyCode } from '@tectonics/prs/money/amount';
import { type Amount } from '@tectonics/prs/money';

export class GoogleAnalytics extends AnalyticsProvider {
  incomingPayment: IncomingPayment;

  init(incomingPayment: IncomingPayment): boolean {
    super.init(incomingPayment);

    if (!window.ga) return false;

    return true;
  }

  event(eventType: AnalyticsEventType) {
    switch (eventType) {
      case 'pageView':
        this.onPageView();
        break;

      case 'error':
        this.onError();
        break;

      case 'voucherGenerate':
        this.onVoucherGenerate();
        break;
    }
  }

  eventOnce(eventType: AnalyticsEventType, incomingPayment: IncomingPayment) {
    const hash = getHash(incomingPayment);
    if (!hasBeenSent(hash)) {
      this.event(eventType);
      saveHasBeenSent(hash);
    }
  }

  onPageView() {
    const { trackingIds, trackingSite } = this.getTrackingData();

    const url = this.getUrl();

    trackingIds.forEach((trackingId, i) => {
      const trackingName = `trackingId${i}`;

      window.ga('create', `${trackingId}`, { name: `${trackingName}`, cookieDomain: 'auto', allowLinker: true });

      if (trackingSite) {
        window.ga('require', 'linker');
        window.ga('linker:autoLink', [`${trackingSite}`]);
      }
      window.ga(`${trackingName}.set`, 'page', url);
      window.ga(`${trackingName}.send`, 'pageview');
    });

    window.ga('gtm1.set', 'page', `${url}`);
  }

  onVoucherGenerate() {
    const { trackingIds } = this.getTrackingData();
    const amount = this.getAmount();

    trackingIds.forEach((trackingId, i) => {
      window.ga(
        `trackingId${i}.send`,
        'event',
        'Payment',
        'voucherGenerate',
        getAmountCurrencyCode(amount),
        Number(getAmountValue(amount))
      );
    });
  }

  onError() {
    window.ga(
      'gtm1.send',
      'event',
      'Errors',
      'ErrorIndicator',
      this.submitError ? this.submitError.message : '',
      { nonInteraction: false }
    );
  }

  getAmount(): Amount {
    const incomingPayment = this.incomingPayment;
    const receipts = getReceipts(incomingPayment);
    const receipt = getMainReceipt(receipts);
    return getReceiptAmountExt(receipt);
  }

  getTrackingData(): { trackingIds: ImmutableArray<string>, trackingSite: ?string } {
    const merchant = getMerchant(this.incomingPayment);
    const trackingIds = getGoogleAnalyticsTrackingIds(merchant);
    const trackingSite = getGoogleAnalyticsTrackingSite(merchant);
    return { trackingIds, trackingSite };
  }

  getUrl(): string {
    const hash = getHash(this.incomingPayment);
    const status = getStatus(this.incomingPayment);

    const statusMap = {
      CA: 'canceled',
      OP: 'opened',
      PE: 'pending',
      CO: 'confirmed',
    };

    return `${hash}/${statusMap[status]}`;
  }
}

function saveHasBeenSent(hash: string) {
  localStorage.setItem(getHasBeenSentKey(hash), 'yes');
}

function hasBeenSent(hash: string): boolean {
  return localStorage.getItem(getHasBeenSentKey(hash)) === 'yes';
}

function getHasBeenSentKey(hash: string): string {
  return `tectonics:ga:${hash}:has_been_sent`;
}
